import { Head, router, usePage } from "@inertiajs/react"
import {
  Briefcase,
  Calendar,
  MapPin,
  SlidersHorizontal,
  Users,
} from "lucide-react"
import { useRef, useState } from "react"

import LocationSelect from "@/components/location-select"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import type { OpportunitiesIndex } from "@/types/serializers"

const POSITIONS = [
  { value: "portero", label: "Portero" },
  { value: "defensa_central", label: "Defensa Central" },
  { value: "lateral_derecho", label: "Lateral Derecho" },
  { value: "lateral_izquierdo", label: "Lateral Izquierdo" },
  { value: "mediocampista_central", label: "Mediocampista Central" },
  { value: "mediocampista_ofensivo", label: "Mediocampista Ofensivo" },
  { value: "mediocampista_defensivo", label: "Mediocampista Defensivo" },
  { value: "extremo_derecho", label: "Extremo Derecho" },
  { value: "extremo_izquierdo", label: "Extremo Izquierdo" },
  { value: "delantero_centro", label: "Delantero Centro" },
  { value: "segundo_delantero", label: "Segundo Delantero" },
]

function positionLabel(value: string): string {
  return POSITIONS.find((p) => p.value === value)?.label ?? value
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export default function OpportunitiesIndex({
  opportunities,
  pagination,
  filters,
}: OpportunitiesIndex) {
  const page = usePage()
  const auth = page.props.auth as { user: { role: string } }
  const userRole = auth?.user?.role
  const [country, setCountry] = useState(filters.country ?? "")
  const [region, setRegion] = useState(filters.region ?? "")
  const prevCountryRef = useRef(filters.country ?? "")

  function applyFilters(newFilters: Partial<OpportunitiesIndex["filters"]>) {
    const merged = { ...filters, ...newFilters }
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(merged)) {
      if (value) params[key] = value
    }
    router.visit("/oportunidades", { data: params, preserveState: true })
  }

  function handleCountryChange(value: string) {
    setCountry(value)
    setRegion("")
    prevCountryRef.current = value
    applyFilters({ country: value, region: "" })
  }

  function handleRegionChange(value: string) {
    setRegion(value)
    applyFilters({ region: value })
  }

  function clearFilters() {
    setCountry("")
    setRegion("")
    router.visit("/oportunidades", { preserveState: true })
  }

  return (
    <AppLayout>
      <Head title="Oportunidades | KrownFT" />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Oportunidades</h1>
            {(userRole === "club" || userRole === "scout") && (
              <Button onClick={() => router.visit("/oportunidades/new")}>
                Crear oportunidad
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-6 lg:flex-row">
            {/* Filter sidebar */}
            <aside className="w-full shrink-0 lg:w-64">
              <div className="border-border bg-card rounded-lg border p-4">
                <div className="mb-4 flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  <h2 className="font-semibold">Filtros</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="mb-1.5 block text-sm">Posición</Label>
                    <Select
                      value={filters.position ?? ""}
                      onValueChange={(v) => applyFilters({ position: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <LocationSelect
                    country={country}
                    region={region}
                    onCountryChange={handleCountryChange}
                    onRegionChange={handleRegionChange}
                    showCity={false}
                  />

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={clearFilters}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              </div>
            </aside>

            {/* Results */}
            <div className="flex-1">
              <p className="text-muted-foreground mb-4 text-sm">
                {pagination.count === 1
                  ? "1 oportunidad encontrada"
                  : `${pagination.count} oportunidades encontradas`}
              </p>

              {opportunities.length > 0 ? (
                <div className="space-y-4">
                  {opportunities.map((opp) => (
                    <div
                      key={opp.id}
                      className="border-border bg-card hover:border-krown-300 cursor-pointer rounded-lg border p-4 transition-colors"
                      onClick={() => router.visit(`/oportunidades/${opp.id}`)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <Briefcase className="text-krown-crown h-4 w-4" />
                            <span className="text-krown-100 text-sm font-medium">
                              {opp.club_profile.name}
                            </span>
                            <VerifiedBadge
                              show={opp.club_profile.verified}
                              size="sm"
                            />
                          </div>
                          <h3 className="mb-2 text-base font-semibold text-white">
                            {opp.title}
                          </h3>
                          <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-3 text-xs">
                            <span className="bg-krown-200/20 text-krown-100 rounded-full px-2 py-0.5 font-medium">
                              {positionLabel(opp.position_needed)}
                            </span>
                            {opp.country && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {[opp.region, opp.country]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}
                            {(opp.min_age ?? opp.max_age) && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {opp.min_age && opp.max_age
                                  ? `${opp.min_age}-${opp.max_age} años`
                                  : opp.min_age
                                    ? `Desde ${opp.min_age} años`
                                    : `Hasta ${opp.max_age} años`}
                              </span>
                            )}
                            {opp.expires_at && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Vence: {formatDate(opp.expires_at)}
                              </span>
                            )}
                          </div>
                          <p className="text-muted-foreground line-clamp-2 text-sm">
                            {opp.description}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-krown-grey-50 text-xs">
                            {formatDate(opp.created_at)}
                          </span>
                          <span className="text-krown-grey-50 flex items-center gap-1 text-xs">
                            <Users className="h-3 w-3" />
                            {opp.applications_count}
                          </span>
                          {userRole === "player" && (
                            <button
                              className="from-krown-orange-from to-krown-orange-to mt-1 rounded-full bg-gradient-to-r px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                              onClick={(e) => {
                                e.stopPropagation()
                                router.post(
                                  `/oportunidades/${opp.id}/postulaciones`,
                                )
                              }}
                            >
                              Postularme
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Briefcase className="text-krown-grey-50 mx-auto mb-4 h-12 w-12" />
                  <p className="text-muted-foreground">
                    No hay oportunidades disponibles en este momento.
                  </p>
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="mt-6 flex justify-center gap-2">
                  {Array.from(
                    { length: pagination.pages },
                    (_, i) => i + 1,
                  ).map((p) => (
                    <Button
                      key={p}
                      variant={p === pagination.page ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        applyFilters({ ...filters, page: String(p) })
                      }
                    >
                      {p}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
